/**
 * Key recorder (W2-13 settings-keymap; DESIGN.md §4.20, §5.15, §8.1): records one shortcut for a command.
 *
 *  - Press the keys: the physical key is recorded (`KeyboardEvent.code`, so Q W E R stay under the same fingers
 *    on AZERTY / QWERTZ); "Match the typed character" binds the produced character instead (`char:?`), "Press and
 *    hold" makes a hold binding (`hold:`). Modifiers held so far are shown live; Esc cancels.
 *  - Refused with the reason: chords the browser or the system keeps (RESERVED_CHORDS on every OS, the macOS list
 *    on a Mac, where Ctrl means Cmd) and Ctrl+Alt (AltGr). Unreliable chords are accepted with a warning.
 *  - Conflicts are explained before saving: red = another command in the same situation (by default the shortcut
 *    is taken from it, so the conflict is resolved), yellow = another command in a different situation (the
 *    situation decides).
 *  - After a key is recorded the box stops listening (Tab and Enter work again); activating it records again.
 *
 * `KeyRecorder` is rendered directly by the keymap editor (it knows the binding's condition and arguments);
 * `KeyRecorderDialog` is the registered `DIALOG.keymapRecorder` for other packages, resolving `{ chord }` after the
 * conflicts it was asked to resolve have been removed. The caller adds or replaces the binding.
 */

import { useId, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { AppServices, DialogProps, KeyBinding } from '../../app/registry/types';
import type { Json } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import { Button, Checkbox, Dialog, Icon, Kbd } from '../../ui';
import { useSettingsValue, useText, type SettingsText } from './hooks';
import {
  bindingTemplate,
  verdictOf,
  chordParts,
  isHoldChord,
  isRefused,
  isUserBinding,
  keymapOs,
  normChord,
  printableChar,
  recordedChord,
  removeBinding,
  type ChordReason,
  type ChordVerdict,
} from './keymapModel';
import { commandTitle, contextText } from './keymapView';
import { m } from './settings.messages';
import s from './Settings.module.css';

export const REASON_MESSAGES: Readonly<Record<ChordReason, MessageKey>> = {
  tabsWindows: m.reasonTabsWindows,
  tabSwitch: m.reasonTabSwitch,
  history: m.reasonHistory,
  closeWindow: m.reasonCloseWindow,
  fullscreen: m.reasonFullscreen,
  devtools: m.reasonDevtools,
  privateWindow: m.reasonPrivateWindow,
  addons: m.reasonAddons,
  browserTool: m.reasonBrowserTool,
  screenshot: m.reasonScreenshot,
  reload: m.reasonReload,
  clearData: m.reasonClearData,
  macApp: m.reasonMacApp,
  macWindow: m.reasonMacWindow,
  macPrefs: m.reasonMacPrefs,
  macCycle: m.reasonMacCycle,
  macHistory: m.reasonMacHistory,
  macAddress: m.reasonMacAddress,
  macLogout: m.reasonMacLogout,
  macOther: m.reasonMacOther,
  ctrlAlt: m.reasonCtrlAlt,
  unreliableMenu: m.reasonUnreliableMenu,
  unreliableAddress: m.reasonUnreliableAddress,
  unreliableDownloads: m.reasonUnreliableDownloads,
  unreliableHistory: m.reasonUnreliableHistory,
  unreliableOther: m.reasonUnreliableOther,
};

/** Translated explanation of a refused or warned chord. */
export function verdictText(text: SettingsText, keymap: AppServices['keymap'], v: ChordVerdict): string {
  if (v.status === 'ok') return '';
  if (v.ref !== undefined) return text.tr(v.ref);
  return text.t(REASON_MESSAGES[v.reason], { chord: keymap.label(v.trigger) });
}

export interface RecorderResult {
  readonly chord: string;
}

export interface KeyRecorderProps {
  readonly services: AppServices;
  readonly open: boolean;
  readonly command: string;
  readonly args?: Json | undefined;
  /** Condition of the new binding (the replaced binding's, or the command's usual one). */
  readonly when?: string | undefined;
  /** The binding being changed, if any. */
  readonly current?: KeyBinding | null | undefined;
  onClose(result: RecorderResult | null): void;
}

interface Captured {
  readonly chord: string;
  readonly char: string | null;
}

interface Mods {
  readonly ctrl: boolean;
  readonly alt: boolean;
  readonly shift: boolean;
}

const NO_MODS: Mods = { ctrl: false, alt: false, shift: false };

function modsOf(e: ReactKeyboardEvent, mac: boolean): Mods {
  return { ctrl: mac ? e.metaKey : e.ctrlKey || e.metaKey, alt: e.altKey, shift: e.shiftKey };
}

export function KeyRecorder(props: KeyRecorderProps) {
  const { services } = props;
  const text = useText(services);
  const { t, tr } = text;
  // Re-render when the keymap's settings change (conflicts are computed against the live keymap).
  useSettingsValue(services);
  const os = keymapOs(services.keymap);
  const current = props.current ?? null;
  const cmd = services.registry.command(props.command);
  const title = commandTitle(t, tr, cmd, props.command, props.args);
  const statusId = useId();

  const [listening, setListening] = useState(true);
  const [captured, setCaptured] = useState<Captured | null>(null);
  const [mods, setMods] = useState<Mods>(NO_MODS);
  const [byChar, setByChar] = useState(current !== null && chordParts(current.chord)?.kind === 'char');
  const usesHold = services.keymap.bindings().some((b) => b.command === props.command && isHoldChord(b.chord));
  const [hold, setHold] = useState(current === null ? usesHold : isHoldChord(current.chord));
  const [takeOver, setTakeOver] = useState(true);

  const chord = captured === null ? null : recordedChord(captured.chord, { byChar, hold, char: captured.char });
  const verdict = chord === null ? null : verdictOf(services.keymap, chord, os);
  const refused = verdict !== null && isRefused(verdict);
  const same = chord !== null && current !== null && normChord(chord) === normChord(current.chord);
  const candidate: KeyBinding | null =
    chord === null
      ? null
      : {
          chord,
          command: props.command,
          ...(props.args === undefined ? {} : { args: props.args }),
          ...(props.when === undefined ? {} : { when: props.when }),
        };
  const conflicts =
    candidate === null || refused || same
      ? []
      : services.keymap.conflicts(candidate).map((c) => {
          const other = services.registry.command(c.with.command);
          return {
            level: c.level,
            with: c.with,
            title: commandTitle(t, tr, other, c.with.command, c.with.args),
            context: contextText(t, c.with.when, other?.when),
          };
        });
  const red = conflicts.filter((c) => c.level === 'red');
  // "Press and hold" is offered for commands that are bound as hold bindings (hold-to-play); commands of kind
  // 'hold' always run on press and release.
  const canHold = cmd?.kind !== 'hold' && usesHold;
  const parts = chord === null ? null : chordParts(chord);
  const canByChar = captured !== null && captured.char !== null && chordParts(captured.chord)?.kind === 'key';

  const saveReason: MessageKey | null =
    chord === null ? m.recorderNeedKey : refused ? m.recorderRefusedSave : same ? m.recorderSameSave : null;

  const onKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>): void => {
    if (!listening) return;
    // Escape is the dialog's (it cancels); everything else is recorded, Tab and Enter included.
    if (e.key === 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    if (e.repeat) return;
    const recorded = services.keymap.chordOf(e.nativeEvent);
    if (recorded === null) {
      setMods(modsOf(e, os === 'mac'));
      return;
    }
    setCaptured({ chord: normChord(recorded), char: printableChar(e.key) });
    setMods(NO_MODS);
    setListening(false);
  };
  const onKeyUp = (e: ReactKeyboardEvent<HTMLButtonElement>): void => {
    if (listening) setMods(modsOf(e, os === 'mac'));
  };
  const restart = (): void => {
    setCaptured(null);
    setListening(true);
    setMods(NO_MODS);
  };

  const save = (): void => {
    if (chord === null || saveReason !== null) return;
    if (takeOver && red.length > 0) {
      // Take the shortcut away from the commands it would clash with (their additions are dropped, their preset
      // bindings removed), so no red conflict is left behind.
      let next = [...services.settings.value.keyboard.overrides];
      for (const c of red) {
        next = removeBinding(next, { binding: c.with, source: isUserBinding(c.with, next) ? 'user' : 'preset' });
      }
      services.keymap.setOverrides(next);
    }
    props.onClose({ chord });
  };

  const liveMods = [
    mods.ctrl ? t(os === 'mac' ? m.modCmd : m.modCtrl) : null,
    mods.alt ? t(os === 'mac' ? m.modOption : m.modAlt) : null,
    mods.shift ? t(m.modShift) : null,
  ]
    .filter((x): x is string => x !== null)
    .join('+');

  return (
    <Dialog
      open={props.open}
      size="md"
      title={t(current === null ? m.recorderTitleAdd : m.recorderTitleChange, { command: title })}
      description={t(m.recorderDesc)}
      onClose={() => {
        props.onClose(null);
      }}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              props.onClose(null);
            }}
          >
            {t(m.cancel)}
          </Button>
          <Button
            variant="primary"
            icon="Check"
            disabledReason={saveReason === null ? null : { key: saveReason }}
            onClick={save}
          >
            {t(red.length > 0 && takeOver ? m.recorderSaveTakeOver : m.recorderSave)}
          </Button>
        </>
      }
    >
      <div className={s.recorder}>
        {current !== null && (
          <p className={s.recorderReplaces}>
            {t(m.recorderReplaces)} <Kbd chord={current.chord} />
          </p>
        )}
        <button
          type="button"
          // Focused on open, before the dialog would focus its first button, so the first key press is recorded.
          autoFocus
          className={s.capture}
          data-listening={listening ? 'true' : undefined}
          data-keys="native"
          aria-describedby={statusId}
          aria-label={
            listening
              ? t(m.recorderListeningLabel)
              : t(m.recorderCapturedLabel, { chord: chord === null ? '' : services.keymap.label(chord) })
          }
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onClick={() => {
            if (!listening) restart();
          }}
        >
          {listening ? (
            <span className={s.captureListening}>
              <Icon name="Keyboard" size={18} />
              {liveMods === '' ? t(m.recorderPrompt) : t(m.recorderHolding, { mods: liveMods })}
            </span>
          ) : chord !== null ? (
            <Kbd chord={chord} />
          ) : null}
        </button>
        <p className={s.recorderHint}>{t(listening ? m.recorderHintListening : m.recorderHintCaptured)}</p>
        <div id={statusId} className={s.recorderStatus} aria-live="polite">
          {verdict !== null && verdict.status !== 'ok' && (
            <p className={s.verdict} data-level={refused ? 'error' : 'warning'} role={refused ? 'alert' : undefined}>
              <Icon name={refused ? 'Ban' : 'TriangleAlert'} size={16} />
              <span>{verdictText(text, services.keymap, verdict)}</span>
            </p>
          )}
          {same && (
            <p className={s.verdict} data-level="info">
              <Icon name="Info" size={16} />
              <span>{t(m.recorderSame)}</span>
            </p>
          )}
          {verdict !== null && !refused && !same && conflicts.length === 0 && (
            <p className={s.verdict} data-level="ok">
              <Icon name="CircleCheck" size={16} />
              <span>{t(m.recorderFree)}</span>
            </p>
          )}
          {conflicts.map((c, i) => (
            <p
              key={`${c.with.command}:${String(i)}`}
              className={s.verdict}
              data-level={c.level === 'red' ? 'error' : 'info'}
            >
              <Icon name={c.level === 'red' ? 'TriangleAlert' : 'Info'} size={16} />
              <span>
                {c.level === 'red'
                  ? t(m.recorderConflictRed, { command: c.title, context: c.context })
                  : t(m.recorderConflictYellow, { command: c.title, context: c.context })}
              </span>
            </p>
          ))}
        </div>
        {red.length > 0 && (
          <Checkbox
            label={t(m.recorderTakeOver, { count: red.length, command: red[0]?.title ?? '' })}
            checked={takeOver}
            onChange={setTakeOver}
          />
        )}
        <div className={s.recorderOptions}>
          <Checkbox
            label={t(m.recorderByChar)}
            checked={byChar && canByChar}
            disabledReason={canByChar ? null : { key: m.recorderByCharUnavailable }}
            onChange={setByChar}
          />
          {canHold && (
            <Checkbox
              label={t(m.recorderHold)}
              checked={hold && parts?.kind !== 'char'}
              disabledReason={parts?.kind === 'char' ? { key: m.recorderHoldUnavailable } : null}
              onChange={setHold}
            />
          )}
        </div>
      </div>
    </Dialog>
  );
}

/**
 * `DIALOG.keymapRecorder`: records a shortcut for `command` (replacing `current`), resolves `{ chord }` or null. The
 * binding's arguments and condition are the opener's (KeymapRecorderProps.args / when, CR-W2-13-4), else derived from
 * `current` or the command's first binding.
 */
export function KeyRecorderDialog(props: DialogProps<'keymapRecorder'>) {
  const { services } = props;
  const tmpl = bindingTemplate(services.keymap.bindings(), props.props.command, props.props.current);
  return (
    <KeyRecorder
      services={services}
      open
      command={props.props.command}
      args={props.props.args ?? tmpl.args}
      when={props.props.when ?? tmpl.when}
      current={tmpl.replacing}
      onClose={(r) => {
        props.close(r === null ? null : { chord: r.chord });
      }}
    />
  );
}
